import { Button } from '@ophelos/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ophelos/ui/components/dropdown-menu';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle } from '@ophelos/ui/components/empty';
import { Skeleton } from '@ophelos/ui/components/skeleton';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from '@ophelos/ui/components/table';
import {
  type ColumnDef,
  type ExpandedState,
  flexRender,
  forestTableFeatures,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  useTable,
  type VisibilityState,
} from '@ophelos/ui/lib/table';
import { cn } from '@ophelos/ui/lib/utils';
import { ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Grid2x2X, Loader2, X } from 'lucide-react';
import * as React from 'react';

const alignClasses = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right [&>div]:ml-auto [&>button]:ml-auto [&>span]:ml-auto',
} as const;

interface DataTableProps<TData extends RowData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  /** Toolbar rendered above the table. Use `<DataTableToolbar>` for the standard layout. */
  toolbar?: React.ReactNode;
  fullWidth?: boolean;
  onRowClick?: (row: TData, event: React.MouseEvent) => void;
  // Row selection/highlighting
  getRowId?: (row: TData) => string;
  selectedRowId?: string | null;
  /** Row (by `getRowId`) to flash with the `highlight-pulse` animation — a transient ~2s accent fade. */
  pulseRowId?: string | null;
  // Server-side pagination props
  manualPagination?: boolean;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  pageCount?: number;
  rowCount?: number;
  hidePagination?: boolean; // Hide pagination UI even when pagination is enabled
  /**
   * Render every row on a single page (no client pagination). Use for tables that live inside a
   * scroll container and must show all rows — notably expandable tables, where the default 10-row
   * page counts expanded sub-rows and would silently push sibling rows onto an unreachable page.
   */
  disablePagination?: boolean;
  // Client-side pagination props
  initialPagination?: PaginationState;
  pageSizeOptions?: number[];
  // Loading states
  isLoading?: boolean;
  isFetching?: boolean;
  // Server-side sorting props
  manualSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
  // Feature toggles
  enableSorting?: boolean;
  /** Renders a column-visibility dropdown above the table when no `toolbar` is provided. */
  enableColumnVisibility?: boolean;
  defaultColumnVisibility?: VisibilityState;
  /** When set, column visibility is persisted to localStorage under this key. */
  persistenceKey?: string;
  // Controlled row selection
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (rowSelection: RowSelectionState) => void;
  // Row density
  rowSize?: 'default' | 'sm' | 'xs';
  // Expandable sub-rows: when `getSubRows` is provided, a row's children render as normal
  // (dimmed) rows under the same columns when the row is expanded.
  getSubRows?: (row: TData) => TData[] | undefined;
  // Group-by control: when `groupByOptions` is provided, a dropdown renders next to the
  // Columns button. The actual grouping is the caller's responsibility (data layer); this
  // is a presentational, controlled selector.
  groupByOptions?: { label: string; value: string; section?: string }[];
  groupByValue?: string;
  onGroupByChange?: (value: string) => void;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

export function DataTable<TData extends RowData, TValue>({
  columns,
  data,
  toolbar,
  fullWidth,
  onRowClick,
  getRowId,
  selectedRowId,
  pulseRowId,
  manualPagination = false,
  pagination,
  onPaginationChange,
  pageCount,
  rowCount,
  hidePagination = false,
  disablePagination = false,
  initialPagination,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  isLoading = false,
  isFetching = false,
  manualSorting = false,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  enableSorting = true,
  enableColumnVisibility = true,
  defaultColumnVisibility,
  persistenceKey,
  rowSelection: controlledRowSelection,
  onRowSelectionChange: controlledOnRowSelectionChange,
  rowSize = 'default',
  getSubRows,
  groupByOptions,
  groupByValue,
  onGroupByChange,
}: DataTableProps<TData, TValue>) {
  const [localSorting, setLocalSorting] = React.useState<SortingState>([]);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const visibilityStorageKey = persistenceKey ? `forest:data-table:columns:${persistenceKey}` : null;
  const [columnVisibility, setColumnVisibilityState] = React.useState<VisibilityState>(() => {
    const base = defaultColumnVisibility ?? {};
    if (!visibilityStorageKey || typeof window === 'undefined') return base;
    try {
      const stored = window.localStorage.getItem(visibilityStorageKey);
      if (!stored) return base;
      const parsed = JSON.parse(stored) as VisibilityState;
      return { ...base, ...parsed };
    } catch {
      return base;
    }
  });
  const setColumnVisibility = React.useCallback<React.Dispatch<React.SetStateAction<VisibilityState>>>(
    (updater) => {
      setColumnVisibilityState((prev) => {
        const next = typeof updater === 'function' ? (updater as (old: VisibilityState) => VisibilityState)(prev) : updater;
        if (visibilityStorageKey && typeof window !== 'undefined') {
          try {
            window.localStorage.setItem(visibilityStorageKey, JSON.stringify(next));
          } catch {
            // ignore quota/serialization errors
          }
        }
        return next;
      });
    },
    [visibilityStorageKey]
  );
  const [localRowSelection, setLocalRowSelection] = React.useState<RowSelectionState>({});
  const [localPagination, setLocalPagination] = React.useState<PaginationState>(() => initialPagination ?? { pageIndex: 0, pageSize: 10 });

  // Use controlled row selection if provided, otherwise use local state
  const rowSelection = controlledRowSelection !== undefined ? controlledRowSelection : localRowSelection;
  const setRowSelection = React.useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      if (controlledOnRowSelectionChange) {
        const newValue = typeof updater === 'function' ? updater(controlledRowSelection || {}) : updater;
        controlledOnRowSelectionChange(newValue);
      } else {
        setLocalRowSelection(updater);
      }
    },
    [controlledOnRowSelectionChange, controlledRowSelection]
  );

  // Use controlled sorting if provided, otherwise use local state
  const sorting = controlledSorting !== undefined ? controlledSorting : localSorting;
  const setSorting = React.useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      if (controlledOnSortingChange) {
        const newValue = typeof updater === 'function' ? updater(controlledSorting || []) : updater;
        controlledOnSortingChange(newValue);
      } else {
        setLocalSorting(updater);
      }
    },
    [controlledOnSortingChange, controlledSorting]
  );

  const table = useTable({
    features: forestTableFeatures,
    data,
    columns: columns as ColumnDef<TData>[],
    ...(getRowId && { getRowId }),
    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: pageCount ?? Math.ceil((rowCount ?? 0) / (pagination?.pageSize ?? 10)),
        }
      : disablePagination
        ? { manualPagination: true }
        : { onPaginationChange: setLocalPagination }),
    ...(enableSorting && {
      onSortingChange: setSorting,
      ...(manualSorting && { manualSorting: true }),
    }),
    ...(getSubRows && {
      getSubRows,
      onExpandedChange: setExpanded,
    }),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      ...(enableSorting && { sorting }),
      ...(getSubRows && { expanded }),
      columnVisibility,
      rowSelection,
      pagination: disablePagination
        ? { pageIndex: 0, pageSize: Number.POSITIVE_INFINITY }
        : manualPagination
          ? (pagination ?? { pageIndex: 0, pageSize: 10 })
          : localPagination,
    },
    debugTable: false,
    debugHeaders: false,
    debugColumns: false,
  });

  const cellPadding = rowSize === 'sm' ? 'py-2.25 px-3' : rowSize === 'xs' ? 'py-1.75 px-3' : 'p-3';
  const showFooter =
    !isLoading && table.getRowModel().rows.length > 0 && table.getAllLeafColumns().some((column) => column.columnDef.footer);

  return (
    <div className="w-full @container">
      {toolbar
        ? toolbar
        : (enableColumnVisibility || (groupByOptions && groupByOptions.length > 0)) && (
            <div className={`${fullWidth ? 'p-3' : 'mb-4'} gap-2 flex items-center justify-end`}>
              {groupByOptions && groupByOptions.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Group by: {groupByOptions.find((o) => o.value === groupByValue)?.label ?? groupByOptions[0]?.label}
                      <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto slim-scrollbar"
                  >
                    <DropdownMenuRadioGroup value={groupByValue} onValueChange={(value) => onGroupByChange?.(value)}>
                      {groupByOptions.map((option, i) => {
                        // A section header is shown the first time a new section appears, so a long
                        // list (base columns vs metadata fields) reads as grouped rather than flat.
                        const showHeader = !!option.section && option.section !== groupByOptions[i - 1]?.section;
                        return (
                          <React.Fragment key={option.value}>
                            {showHeader && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel className="text-muted-foreground text-xs font-normal">
                                  {option.section}
                                </DropdownMenuLabel>
                              </>
                            )}
                            <DropdownMenuRadioItem value={option.value}>{option.label}</DropdownMenuRadioItem>
                          </React.Fragment>
                        );
                      })}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              {enableColumnVisibility && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      Columns <ChevronDown className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-(--radix-dropdown-menu-content-available-height) overflow-y-auto slim-scrollbar"
                  >
                    {table
                      .getAllColumns()
                      .filter((column) => column.getCanHide())
                      .map((column) => (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) => column.toggleVisibility(!!value)}
                        >
                          {typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id}
                        </DropdownMenuCheckboxItem>
                      ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          )}

      <div className={cn(fullWidth ? 'rounded-none border-b border-border-light' : 'rounded-md border', 'relative overflow-hidden')}>
        {isFetching && !isLoading && (
          <div className="absolute inset-0 bg-background/50 backdrop-blur-sm z-10 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}
        <Table>
          <TableHeader className={`${fullWidth ? 'bg-background' : 'bg-soft dark:bg-card'}`}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="border-b border-border">
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className={` ${fullWidth && 'first:pl-4 text-muted-foreground'} px-3 ${alignClasses[header.column.columnDef.meta?.align ?? 'left']}`}
                      style={header.column.columnDef.size ? { width: header.column.getSize() } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className={`${fullWidth ? 'bg-background' : 'bg-card dark:bg-background'}`}>
            {isLoading ? (
              // Skeleton loading state
              Array.from({ length: 5 }, (_, i) => ({ rowId: `skeleton-row-${i}`, cellCount: table.getVisibleLeafColumns().length })).map(
                ({ rowId, cellCount }) => (
                  <TableRow key={rowId}>
                    {Array.from({ length: cellCount }, (_, j) => ({ cellId: `${rowId}-cell-${j}` })).map(({ cellId }) => (
                      <TableCell key={cellId}>
                        <Skeleton className="h-6 w-full" />
                      </TableCell>
                    ))}
                  </TableRow>
                )
              )
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const isSelected = selectedRowId !== undefined && row.id === selectedRowId;
                const isPulsing = pulseRowId != null && row.id === pulseRowId;
                const isChild = row.depth > 0;
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() || isSelected ? 'selected' : undefined}
                    onClick={(event) => {
                      // Clicking anywhere on an expandable parent toggles it (the chevron stops
                      // propagation, so it never double-toggles); leaf rows fall through to onRowClick.
                      if (getSubRows && row.getCanExpand()) row.toggleExpanded();
                      else onRowClick?.(row.original, event);
                    }}
                    className={cn(
                      // Expanded children: dimmed with a left accent bar to reinforce nesting.
                      isChild && 'bg-soft [&>td:first-child]:border-l-2 [&>td:first-child]:border-border-heavy',
                      // A heavier top border divides each top-level group when grouping is active.
                      getSubRows && !isChild && 'border-t border-border',
                      (onRowClick || (getSubRows && row.getCanExpand())) && 'cursor-pointer hover:bg-muted/50 transition-colors',
                      isPulsing && 'highlight-pulse'
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={`${fullWidth && 'first:pl-4'} ${cellPadding} ${alignClasses[cell.column.columnDef.meta?.align ?? 'left']}`}
                        style={cell.column.columnDef.size ? { width: cell.column.getSize() } : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  <Empty>
                    <EmptyHeader>
                      <EmptyMedia variant="default">
                        <div className="p-4.25 border rounded-[2.75rem] bg-soft/50 border-border-light/75">
                          <div className="p-3.75 border rounded-[1.75rem] shadow-lg bg-muted/75 border-border/75 shadow-lg/5">
                            <div className="p-2.75 border rounded-xl shadow-lg bg-card/80 border-border-heavy/80 shadow-lg/10">
                              <Grid2x2X className="text-muted-foreground" />
                            </div>
                          </div>
                        </div>
                      </EmptyMedia>
                      <EmptyTitle>No data found</EmptyTitle>
                    </EmptyHeader>
                  </Empty>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {showFooter ? (
            <TableFooter>
              {table.getFooterGroups().map((footerGroup) => (
                <TableRow key={footerGroup.id} className="border-t border-border">
                  {footerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      className={cn(fullWidth && 'first:pl-4', cellPadding, alignClasses[header.column.columnDef.meta?.align ?? 'left'])}
                      style={header.column.columnDef.size ? { width: header.column.getSize() } : undefined}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableFooter>
          ) : null}
        </Table>
      </div>

      {/* Pagination */}
      {!hidePagination && manualPagination && pagination ? (
        <div className="flex flex-wrap-reverse gap-4 items-center justify-between px-2 py-2">
          <div className="shrink-0 text-sm text-muted-foreground">
            {rowCount ? (
              <>
                Showing {Math.min(pagination.pageIndex * pagination.pageSize + 1, rowCount)} to{' '}
                {Math.min((pagination.pageIndex + 1) * pagination.pageSize, rowCount)} of {rowCount} results
              </>
            ) : (
              'Loading...'
            )}
          </div>
          <div className="flex flex-1 justify-between items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <select
                className="h-8 w-[70px] rounded-md border border-input bg-background px-2 py-1 text-sm"
                value={pagination.pageSize}
                onChange={(e) => {
                  const newPageSize = Number(e.target.value);
                  onPaginationChange?.({
                    pageIndex: 0,
                    pageSize: newPageSize,
                  });
                }}
                disabled={isFetching}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {pagination.pageIndex + 1} of {table.getPageCount() || 1}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => onPaginationChange?.({ ...pagination, pageIndex: 0 })}
                disabled={!table.getCanPreviousPage() || isFetching}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => onPaginationChange?.({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
                disabled={!table.getCanPreviousPage() || isFetching}
              >
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="h-8 w-8 p-0"
                onClick={() => onPaginationChange?.({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
                disabled={!table.getCanNextPage() || isFetching}
              >
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => onPaginationChange?.({ ...pagination, pageIndex: table.getPageCount() - 1 })}
                disabled={!table.getCanNextPage() || isFetching}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : !hidePagination && !manualPagination && !disablePagination ? (
        <div className="flex flex-wrap-reverse gap-4 items-center justify-between px-2 py-2">
          <div className="shrink-0 text-sm text-muted-foreground">
            Showing {Math.min(localPagination.pageIndex * localPagination.pageSize + 1, data.length)} to{' '}
            {Math.min((localPagination.pageIndex + 1) * localPagination.pageSize, data.length)} of {data.length} results
          </div>
          <div className="flex flex-1 justify-between items-center space-x-6 lg:space-x-8">
            <div className="flex items-center space-x-2">
              <p className="text-sm font-medium">Rows per page</p>
              <select
                className="h-8 w-[70px] rounded-md border border-input bg-background px-2 py-1 text-sm"
                value={localPagination.pageSize}
                onChange={(e) => {
                  table.setPageSize(Number(e.target.value));
                }}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex w-[100px] items-center justify-center text-sm font-medium">
              Page {localPagination.pageIndex + 1} of {table.getPageCount() || 1}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
              >
                <span className="sr-only">Go to first page</span>
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
                <span className="sr-only">Go to previous page</span>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-8 w-8 p-0" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
                <span className="sr-only">Go to next page</span>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                className="hidden h-8 w-8 p-0 lg:flex"
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
              >
                <span className="sr-only">Go to last page</span>
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── Toolbar ─────────────────────────────────────────────────────────────
// Standard layout for the `toolbar` slot of DataTable. Search on the left,
// filter pills inline, optional Clear all, trailing right-aligned actions.

interface DataTableToolbarProps {
  /** Left-aligned search element — typically `<SearchBar />`. */
  search?: React.ReactNode;
  /** Filter pills rendered inline after the search. */
  filters?: React.ReactNode;
  /** Right-aligned trailing content — column visibility, secondary actions. */
  trailing?: React.ReactNode;
  /** When provided and `hasActiveFilters` is true, shows a "Clear all" button after the filters. */
  onClearAll?: () => void;
  hasActiveFilters?: boolean;
  className?: string;
  /** Omit the border-b divider between toolbar and table. */
  fullWidth?: boolean;
}

export function DataTableToolbar({ search, filters, trailing, onClearAll, hasActiveFilters, className, fullWidth }: DataTableToolbarProps) {
  const showClear = onClearAll && hasActiveFilters;

  return (
    <div className={cn('flex items-center gap-2 flex-wrap py-3', fullWidth && 'border-b border-border-light', className)}>
      {search}
      {filters}
      {showClear && (
        <Button type="button" variant="ghost" size="sm" className="text-muted-foreground h-7 text-xs" onClick={onClearAll}>
          <X className="size-3" />
          Clear all
        </Button>
      )}
      {trailing && <div className="ml-auto flex items-center gap-2">{trailing}</div>}
    </div>
  );
}
